import { useState } from "react";
import { toast } from "sonner";
import { Send, Database, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatApiError } from "@/lib/api";
import { formatDateTime, formatPoints, onlyDigits } from "@/lib/format";
import { useBranding } from "@/context/BrandingContext";
import { Panel } from "@/components/admin/PageBits";

export const ScorePanel = ({ req, scores, onChanged }) => {
  const { config } = useBranding();
  const [points, setPoints] = useState("");
  const [confirm, setConfirm] = useState(false);
  const locked = req.status === "AGENDAMENTO_SOLICITADO";

  const run = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      setPoints("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendDemo = () => run(() => http.post(`/admin/requests/${req.id}/score`, { points: Number(onlyDigits(points)) }), "Pontuação enviada ao cliente.");
  const official = () => run(() => http.post(`/admin/requests/${req.id}/score/official`), "Pontuação oficial obtida.");
  const unavailable = () => run(() => http.post(`/admin/requests/${req.id}/unavailable`), "Cliente informado sobre a indisponibilidade.");

  return (
    <Panel title="Pontuação Rewards" testId="score-panel">
      {req.score && (
        <div className="mb-4 rounded-sm border border-white/10 bg-black/20 p-3 text-sm" data-testid="current-score">
          <span className="font-display text-lg" style={{ color: "var(--brand)" }}>{formatPoints(req.score.points)} pontos</span>
          <span className="ml-2 rounded bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300">{req.score.mode === "OFICIAL" ? "Fonte oficial" : "Informado"}</span>
          <p className="mt-1 text-xs text-slate-500">{req.score.source} · {formatDateTime(req.score.obtained_at)} · {req.score.operator?.name}</p>
        </div>
      )}
      <div className="space-y-2">
          <p className="text-xs text-slate-400">Informe a quantidade de pontos do cliente.</p>
          <div className="flex gap-2">
            <Input inputMode="numeric" placeholder="Quantidade de pontos" value={points ? formatPoints(onlyDigits(points)) : ""} onChange={(e) => setPoints(onlyDigits(e.target.value).slice(0, 10))} disabled={locked} className="h-10 border-white/10 bg-black/30 font-mono-x text-slate-100" data-testid="points-input" />
            <button disabled={!points || locked} onClick={() => setConfirm(true)} className="btn-gold h-10 shrink-0 px-4 text-sm" data-testid="points-send-btn"><Send className="h-4 w-4" /> Enviar</button>
          </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={!config?.integration_available || locked} onClick={official} title={config?.integration_available ? "" : "Integração oficial não configurada"} className="btn-ghost-sm disabled:opacity-40" data-testid="official-score-btn"><Database className="h-3.5 w-3.5" /> Consultar fonte oficial</button>
        <button disabled={req.status !== "AGUARDANDO_CONSULTA"} onClick={unavailable} className="btn-ghost-sm disabled:opacity-40" data-testid="mark-unavailable-btn"><Ban className="h-3.5 w-3.5" /> Informar indisponibilidade</button>
      </div>
      {scores?.length > 1 && <p className="mt-3 text-xs text-slate-500">{scores.length} envios registrados.</p>}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-white/10 bg-[#141C2E] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar envio</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">O cliente verá imediatamente: "Pontuação Rewards: {formatPoints(onlyDigits(points))} pontos".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-slate-300" data-testid="points-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={sendDemo} className="bg-[var(--brand)] text-black hover:opacity-90" data-testid="points-confirm">Confirmar envio</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
